import { useEmailSuppressionResult } from '@/api/queries/emails/useGetEmailSuppressionResults';
import { useDebounce } from '@payaca/hooks';
import { useState } from 'react';
import { type Control, useWatch } from 'react-hook-form';
import { type Contact } from './contacts';
import { type DraftSendEmailPreviewValues } from './DraftSendEmailPreviewFieldset';
import { type DraftSendEmailState } from './types';

const useDebouncedRecipientEmail = (
  control: Control<DraftSendEmailPreviewValues>
): string => {
  const watchedEmail = useWatch({ control, name: 'recipientEmail' });
  const [debounced, setDebounced] = useState(watchedEmail.trim());
  useDebounce(() => setDebounced(watchedEmail.trim()), 300, [watchedEmail]);
  return debounced;
};

export type DraftSendEmailSuppression = {
  isSuppressed: boolean;
  reason: string | undefined;
};

/**
 * Suppression (blocked-recipient) check for the draft-send email form. In
 * `bounced` state the recipient email is edited inline, so the check follows
 * the (debounced) form value rather than the pinned Contact.
 */
export const useDraftSendEmailSuppression = (params: {
  state: DraftSendEmailState;
  recipient: Contact | null;
  control: Control<DraftSendEmailPreviewValues>;
}): DraftSendEmailSuppression => {
  const { state, recipient, control } = params;
  const debouncedRecoveryEmail = useDebouncedRecipientEmail(control);
  const emailToCheck =
    state === 'bounced' ? debouncedRecoveryEmail : recipient?.email;
  const suppression = useEmailSuppressionResult(emailToCheck);
  return {
    isSuppressed: !!suppression?.isSuppressed,
    reason: suppression?.reason ?? undefined,
  };
};
